import { useState, type MouseEvent } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { ChevronDown, ChevronUp, CircleHelp, LogOut, Menu, User } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useAuthStore } from '@/store/authStore'
import { useSessionStore } from '@/store/sessionStore'
import { usePermissions } from '@/hooks/usePermissions'
import { getProfilePictureUrl } from '@/services/user'
import { userHasPermission } from '@/utils/permissions'

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true'

/**
 * Header.tsx
 * Exact port of Blazor Shared/Components/Header.razor.
 *
 * Structure (left → right):
 *   [Logo] | [App name] [Tenant] | [Dashboard] [Capacity Mgmt] [Attendance] [Reports]
 *                                  [Help ▾]  [Avatar ▾]
 *
 * Role-gated tabs match Blazor RoleManager.UserHasPermission checks exactly:
 *   Dashboard + Capacity Management : CapacityApplication AND tenant = DUWL
 *   Attendance Tracking + Reports   : AttendanceTrackingApplication
 *
 * User dropdown items:
 *   User Management  : UserManagement permission
 *   DU Staff Badging : DUStaffBadging permission AND tenant = DUWL
 *   Log Out          : always
 *
 * Logo: requires public/assets/imgs/logo.png
 *   → copy from Blazor: wwwroot/assets/imgs/logo.png
 */
export function Header() {
  const logoSrc = `${import.meta.env.BASE_URL}assets/imgs/logo.png`
  // Always call useMsal() unconditionally.
  // In DISABLE_AUTH mode, MockAuthProvider ensures it's safe.
  // In production mode, MsalProvider ensures it's safe.
  const msalContext = useMsal()
  const navigate = useNavigate()
  const location     = useLocation()
  const { hasPermission } = usePermissions()
  const { displayName, tenant, email, availableTenants } = useAuthStore()
  const setTenant    = useAuthStore((s) => s.setTenant)
  const tenantRoles    = useAuthStore((s) => s.tenantRoles)
  const setSessionKey = useSessionStore((s) => s.set)
  const accounts     = msalContext.accounts ?? []

  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showHelpMenu, setShowHelpMenu] = useState(false)
  const [showTenantMenu, setShowTenantMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [tenantMenuStyle, setTenantMenuStyle] = useState<{ top?: number; left?: number; minWidth?: number }>({})

  // ── Derived values ──────────────────────────────────────────────────────────
  const userName     = accounts[0]?.name ?? displayName ?? accounts[0]?.username ?? ''
  const userInitials = userName.split(' ').filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase()
  // Profile photo URL from Profiles API (matches Blazor ApplicationConfigs.ProfilesAPI)
  const userEmail    = accounts[0]?.username ?? email ?? ''
  const profileUrl   = getProfilePictureUrl(userEmail)
  // Default to DUWL when no tenant is set (matches Blazor default: Tenant = "DUWL")
  const activeTenant = tenant || 'DUWL'
  const isDuwl       = activeTenant === 'DUWL'

  /**
   * Mirrors Blazor Header.razor showRegionOption:
   *   UserTenants.Count > 1 — but NOT shown on access_denied / certain public pages.
   */
  const isAccessDeniedPage = location.pathname.toLowerCase().includes('access_denied')
  const showRegionOption   = availableTenants.length > 1 && !isAccessDeniedPage

  // ── Permission gates (mirror Blazor RoleManager checks) ────────────────────
  const canAccessCapacity    = hasPermission('CapacityApplication')
  const canAccessAttendance  = hasPermission('AttendanceTrackingApplication')
  const canManageUsers       = hasPermission('UserManagement')
  const canStaffBadge        = hasPermission('DUStaffBadging') && isDuwl

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const closeAll     = () => { setShowUserMenu(false); setShowHelpMenu(false); setShowTenantMenu(false); setShowMobileMenu(false) }
  const handleLogout = () => { closeAll(); if (!DISABLE_AUTH) void msalContext.instance.logoutRedirect() }
  const handleTenantToggle = (event: MouseEvent<HTMLButtonElement>) => {
    const nextValue = !showTenantMenu
    setShowTenantMenu(nextValue)

    if (nextValue) {
      const rect = event.currentTarget.getBoundingClientRect()
      setTenantMenuStyle({
        top: rect.bottom + 4,
        left: rect.left,
        minWidth: Math.max(rect.width, 80),
      })
    }
  }

  /**
   * Mirrors Blazor Header.razor GotoURL() + SetSession():
   *   SetSession saves tenant to session storage, closes region menu.
   *   GotoURL navigates:
   *     DUIN → /attendancetracking
   *     DUWL → /dashboard (CapacityApplication) OR /attendancetracking OR /access_denied
   */
  const handleTenantChange = (newTenant: string) => {
    // Update stores (mirrors Blazor SetSession + Blazored.SessionStorage)
    setTenant(newTenant)
    setSessionKey('tenant', newTenant)
    closeAll()
    // Use tenantRoles directly to avoid stale hasPermission closure from current render
    const rolesForNewTenant = tenantRoles[newTenant] ?? []
    if (newTenant === 'DUIN') {
      navigate('/attendancetracking')
    } else if (newTenant === 'DUWL') {
      if (userHasPermission(rolesForNewTenant, 'CapacityApplication')) {
        navigate('/dashboard')
      } else if (userHasPermission(rolesForNewTenant, 'AttendanceTrackingApplication')) {
        navigate('/attendancetracking')
      } else {
        navigate('/access_denied')
      }
    }
  }

  // Shared NavLink active class — matches Blazor .navactive CSS
  const tabClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex cursor-pointer items-center px-1 sm:px-2 lg:px-3 text-sm sm:text-sm lg:text-[14px] text-white no-underline outline-none whitespace-nowrap',
      isActive ? 'bg-[#26890D] my-1 self-stretch' : 'h-full hover:bg-white/10',
    )

  // ── Navigation structure: tenant-aware primary (always visible on mobile/tablet) + secondary (dropdown on mobile/tablet) ──
  // DUWL mobile/tablet: Dashboard visible, other links in dropdown
  // DUIN mobile/tablet: Attendance Tracking visible, Reports in dropdown
  // Desktop: All links visible
  const primaryLinks = isDuwl
    ? [
        canAccessCapacity && { to: '/dashboard', label: 'Dashboard' },
      ].filter((link): link is { to: string; label: string } => link !== false)
    : [
        canAccessAttendance && { to: '/attendancetracking', label: 'Attendance Tracking' },
      ].filter((link): link is { to: string; label: string } => link !== false)

  const secondaryLinks = isDuwl
    ? [
        canAccessCapacity && { to: '/capacity-management', label: 'Capacity Management' },
        canAccessAttendance && { to: '/attendancetracking', label: 'Attendance Tracking' },
        canAccessAttendance && { to: '/attendancesummary', label: 'Reports' },
      ].filter((link): link is { to: string; label: string } => link !== false)
    : [
        canAccessAttendance && { to: '/attendancesummary', label: 'Reports' },
      ].filter((link): link is { to: string; label: string } => link !== false)

  return (
    <>
      {/* ── Fixed top bar ──────────────────────────────────────────────────── */}
      <header
        className="fixed left-0 top-0 z-[160] flex h-14 w-full items-center justify-between bg-[#080808] overflow-x-hidden overflow-y-visible"
        aria-label="Application header"
      >
        {/* ── Left: logo + app name + nav tabs (flexible width, max width reserved for right) ───────────── */}
        <div className="header-left-container flex h-full flex-1 items-center min-w-0 overflow-visible max-w-[calc(100%-140px)]" style={{ maxWidth: 'calc(100% - 140px)' }}>

          {/* Logo */}
          <div className="flex h-full items-center mx-1 sm:mx-2 lg:mx-4 flex-shrink-0">
            <img
              src={logoSrc}
              alt="Deloitte"
              className="h-4 align-baseline"
              onError={(e) => {
                // Fallback text when logo asset is not yet copied to public/
                const el = e.currentTarget
                el.style.display = 'none'
                const parent = el.parentElement
                if (parent && !parent.querySelector('[data-logo-fallback]')) {
                  const span = document.createElement('span')
                  span.setAttribute('data-logo-fallback', 'true')
                  span.className = 'text-sm font-bold text-white tracking-widest'
                  span.textContent = 'DELOITTE'
                  parent.appendChild(span)
                }
              }}
            />
          </div>

          {/* App name + tenant — border-left separator matches Blazor .application-sub-title-container */}
          <div className="flex h-full items-center border-l border-[#53565a] flex-shrink-0">
            <span className="ml-2 sm:ml-2 lg:ml-[15px] text-sm sm:text-sm lg:text-[16px] text-white whitespace-nowrap">SpaceIQ</span>

            {/*
             * Tenant: dropdown when user has multiple tenants (showRegionOption),
             * static text when only one tenant.
             * Mirrors Blazor Header.razor @if (showRegionOption) { dropdown } else { label }
             */}
            {showRegionOption ? (
              /* Multi-tenant: dropdown toggle + menu */
              <div className="relative ml-3 sm:ml-5">
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-1 border-0 bg-transparent text-white"
                  onClick={handleTenantToggle}
                  aria-label={`Selected region: ${activeTenant}. Click to change.`}
                  aria-expanded={showTenantMenu}
                  aria-haspopup="listbox"
                >
                  <span className="text-sm sm:text-[13px]">{activeTenant}</span>
                  {showTenantMenu ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {showTenantMenu && (
                  <ul
                    className="fixed z-[200] list-none bg-white py-[5px] shadow-[0_2px_4px_0_rgba(0,0,0,0.12)] border border-[#e5e5e5] m-0 p-0 max-h-[calc(100vh-72px)] overflow-y-auto"
                    style={tenantMenuStyle}
                    role="listbox"
                    aria-label="Select region"
                  >
                    {availableTenants.map((t) => (
                      <li
                        key={t}
                        role="option"
                        aria-selected={t === activeTenant}
                        className={cn(
                          'cursor-pointer px-4 py-[6px] text-[13px] text-black hover:bg-[#f6f6f6]',
                          t === activeTenant && 'font-semibold',
                        )}
                        onClick={() => handleTenantChange(t)}
                      >
                        {t}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              /* Single tenant: static label — matches Blazor Region-menu-title */
              activeTenant && (
                <span className="ml-3 sm:ml-5 text-sm sm:text-[13px] text-white">{activeTenant}</span>
              )
            )}
          </div>

          {/* Nav tabs */}
          <nav className="header-nav-desktop flex h-full min-w-0 items-center ml-2 sm:ml-2 lg:ml-10 flex-shrink-0" aria-label="Main navigation">
            {/* Desktop view: all links visible */}
            <div className="hidden lg:flex h-full items-center gap-0">
              {canAccessCapacity && isDuwl && (
                <>
                  <NavLink to="/dashboard"          className={tabClass}>Dashboard</NavLink>
                  <NavLink to="/capacity-management" className={tabClass}>Capacity Management</NavLink>
                </>
              )}
              {canAccessAttendance && (
                <>
                  <NavLink to="/attendancetracking" className={tabClass}>Attendance Tracking</NavLink>
                  <NavLink to="/attendancesummary"  className={tabClass}>Reports</NavLink>
                </>
              )}
            </div>

            {/* Mobile view (<768px): show all links directly without dropdown */}
            {/* Tablet view (768px-1023px): primary links + dropdown menu for secondary */}
            <div className="lg:hidden flex h-full items-center gap-2 sm:gap-1">
              {primaryLinks.map((link) => (
                <NavLink 
                  key={link.to}
                  to={link.to} 
                  className={tabClass}
                >
                  {link.label}
                </NavLink>
              ))}

              {/* Dropdown menu button for secondary links */}
              {secondaryLinks.length > 0 && (
                <div className="relative">
                  <button
                    type="button"
                    className="header-mobile-more-btn flex h-full min-w-9 shrink-0 cursor-pointer items-center justify-center px-2 sm:px-2 text-sm sm:text-sm lg:text-[14px] text-white no-underline outline-none border-0 bg-transparent hover:bg-white/10 lg:justify-start"
                    onClick={() => { setShowMobileMenu((v) => !v); setShowUserMenu(false); setShowHelpMenu(false) }}
                    aria-label="More navigation options"
                    aria-expanded={showMobileMenu}
                    aria-haspopup="true"
                    title="More options"
                  >
                    <span className="header-mobile-more-text mr-1 hidden lg:inline">More</span>
                    <span className="header-mobile-more-icon inline-flex items-center justify-center lg:hidden" aria-hidden="true" data-testid="mobile-more-icon">
                      <Menu size={18} />
                    </span>
                    <span className="header-mobile-more-chevron hidden lg:inline-flex" aria-hidden="true">
                      {showMobileMenu ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </span>
                  </button>

                  {showMobileMenu && (
                    <div
                      className="fixed top-14 z-[200] min-w-[180px] bg-white border border-[#e5e5e5] shadow-[0_2px_4px_0_rgba(0,0,0,0.12)]"
                      role="menu"
                    >
                      {secondaryLinks.map((link) => (
                        <NavLink
                          key={link.to}
                          to={link.to}
                          onClick={() => setShowMobileMenu(false)}
                          className={({ isActive }) =>
                            cn(
                              'block px-3 py-2 text-base text-gray-800 hover:bg-gray-100 no-underline',
                              isActive ? 'bg-green-100 font-semibold' : ''
                            )
                          }
                          role="menuitem"
                        >
                          {link.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </nav>
        </div>

        {/* ── Right: help + user (flexible) ────────────────────────────────────── */}
        <div className="header-right-container flex h-full items-center justify-end flex-shrink-0 gap-1 lg:gap-3 mr-1 lg:mr-0 min-w-[140px]">

          {/* Help icon — matches Blazor .th-header-sub-container.question-menu */}
          <div className="relative mx-0 lg:mx-1">
            <button
              type="button"
              className="flex cursor-pointer items-center border-0 bg-transparent text-white mr-2"
              onClick={() => { setShowHelpMenu((v) => !v); setShowUserMenu(false) }}
              aria-label="Have a Question"
              aria-expanded={showHelpMenu}
              aria-haspopup="true"
              title="Have a Question"
            >
              <CircleHelp size={22} className="lg:w-6 lg:h-6" />
            </button>

            {showHelpMenu && (
              <div
                className="fixed top-14 z-[200] min-w-[170px] bg-white border border-[#e5e5e5] py-[5px] shadow-[0_2px_4px_0_rgba(0,0,0,0.12)]"
                style={{ right: '90px' }}
                role="menu"
              >
                <ul className="m-0 list-none p-0">
                  <li role="none">
                    <a
                      href="https://deloitteus.service-now.com/sp?id=sc_cat_item&sys_id=9d997dc0db16ac10eb90abc5ca961911&table=sc_cat_item"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block no-underline hover:bg-[#f6f6f6]"
                      role="menuitem"
                    >
                      <span className="block py-[2px] text-[13px] leading-5 text-black ml-[15px]">Raise an Incident</span>
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* User avatar + dropdown — matches Blazor #user-dropdown */}
          <div className="relative mx-1 lg:mx-5">
            <button
              type="button"
              className="flex cursor-pointer items-center border-0 bg-transparent"
              onClick={() => { setShowUserMenu((v) => !v); setShowHelpMenu(false) }}
              aria-label="User menu"
              aria-expanded={showUserMenu}
              aria-haspopup="true"
            >
              {/* Avatar: profile photo from Profiles API; falls back to initials circle */}
              {profileUrl ? (
                <img
                  src={profileUrl}
                  alt={userName}
                  aria-hidden="true"
                  className="h-[35px] w-[35px] rounded-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    const sibling = e.currentTarget.nextElementSibling as HTMLElement | null
                    if (sibling) sibling.style.display = 'flex'
                  }}
                />
              ) : null}
              <div
                aria-hidden="true"
                className="flex h-[35px] w-[35px] items-center justify-center rounded-full bg-[#53565a] text-sm font-medium text-white"
                style={{ display: profileUrl ? 'none' : 'flex' }}
              >
                {userInitials ? userInitials : <User size={18} />}
              </div>
              <span className="ml-1 text-white">
                {showUserMenu ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </span>
            </button>

            {showUserMenu && (
              <div
                className="fixed top-14 z-[200] min-w-[240px] rounded-[10px] border border-[#e5e5e5] bg-white shadow-[0_2px_4px_0_rgba(0,0,0,0.12)]"
                style={{ right: '25px' }}
                role="menu"
              >
                <ul className="m-0 list-none p-0">
                  {/* User identity row */}
                  <li className="p-[10px]" role="none">
                    <div className="flex items-center">
                      {/* Profile photo in dropdown — same source as header button avatar */}
                      {profileUrl ? (
                        <img
                          src={profileUrl}
                          alt={userName}
                          className="h-[35px] w-[35px] shrink-0 rounded-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                            const sibling = e.currentTarget.nextElementSibling as HTMLElement | null
                            if (sibling) sibling.style.removeProperty('display')
                          }}
                        />
                      ) : null}
                      <div
                        className="flex h-[35px] w-[35px] shrink-0 items-center justify-center rounded-full bg-[#53565a] text-sm font-medium text-white"
                        style={{ display: profileUrl ? 'none' : undefined }}
                      >
                        {userInitials ? userInitials : <User size={18} />}
                      </div>
                      <div className="ml-[15px]">
                        <div className="text-[13px] font-semibold leading-5 text-black">{userName}</div>
                        <div className="text-[12px] leading-5 text-[#97999b]">Talent</div>
                      </div>
                    </div>
                  </li>

                  {/* User Management */}
                  {canManageUsers && (
                    <li
                      className="cursor-pointer p-[10px] hover:bg-[#f6f6f6]"
                      role="none"
                    >
                      <button
                        type="button"
                        className="w-full border-0 bg-transparent text-left"
                        role="menuitem"
                        onClick={() => { navigate('/user-access-management'); closeAll() }}
                      >
                        <span className="ml-[15px] text-[13px] leading-5 text-black">User Management</span>
                      </button>
                    </li>
                  )}

                  {/* DU Staff Badging */}
                  {canStaffBadge && (
                    <li
                      className="cursor-pointer p-[10px] hover:bg-[#f6f6f6]"
                      role="none"
                    >
                      <button
                        type="button"
                        className="w-full border-0 bg-transparent text-left"
                        role="menuitem"
                        onClick={() => { navigate('/staff-badging'); closeAll() }}
                      >
                        <span className="ml-[15px] text-[13px] leading-5 text-black">DU Staff Badging</span>
                      </button>
                    </li>
                  )}

                  {/* Separator */}
                  <li className="mx-[5%] border-t border-[#e6e6e6]" role="separator" />

                  {/* Log Out */}
                  <li
                    className="cursor-pointer p-[10px] hover:bg-[#f6f6f6]"
                    role="none"
                  >
                    <button
                      type="button"
                      className="flex w-full items-center border-0 bg-transparent text-left"
                      role="menuitem"
                      onClick={handleLogout}
                    >
                      <LogOut size={16} className="text-black" aria-hidden="true" />
                      <span className="ml-[15px] text-[13px] leading-5 text-black">Log Out</span>
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Click-away backdrop — z between page (0) and dropdown (200) */}
      {(showUserMenu || showHelpMenu || showTenantMenu) && (
        <div
          className="fixed inset-0 z-[150]"
          onClick={closeAll}
          aria-hidden="true"
        />
      )}
    </>
  )
}
